import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { useClaimEventsDialogStyles } from './ClaimEventsDialog.styles';

export interface ClaimEventsDialogProps {
  open: boolean;
  eventCount: number;
  isProcessing: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ClaimEventsDialog: React.FC<ClaimEventsDialogProps> = ({
  open,
  eventCount,
  isProcessing,
  error,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation('planner');
  const styles = useClaimEventsDialogStyles();

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && !isProcessing && onCancel()}>
      <DialogSurface
        aria-describedby="claim-dialog-body"
        style={{ width: '37.5rem', borderRadius: '0.5rem' }}
      >
        <DialogBody>
          <DialogTitle
            action={
              <BasicButton
                variant="subtle"
                size="medium"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onCancel}
                data-testid="claim-dialog-dismiss"
              />
            }
          >
            {t('dialogs.claim.title')}
          </DialogTitle>
          <DialogContent id="claim-dialog-body" className={styles.content}>
            {t('dialogs.claim.body', { count: eventCount })}
            {error && (
              <p
                role="alert"
                style={{ color: 'var(--colorPaletteRedForeground1)', marginTop: '0.5rem' }}
              >
                {error}
              </p>
            )}
          </DialogContent>
          <DialogActions>
            <BasicButton
              variant="primary"
              size="medium"
              onClick={onConfirm}
              disabled={isProcessing}
              icon={isProcessing ? <Spinner size="tiny" /> : undefined}
              data-testid="claim-dialog-confirm"
            >
              {isProcessing ? t('dialogs.claim.processing') : t('dialogs.claim.confirm')}
            </BasicButton>
            <BasicButton
              variant="secondary"
              size="medium"
              onClick={onCancel}
              data-testid="claim-dialog-cancel"
            >
              {t('dialogs.claim.cancel')}
            </BasicButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

ClaimEventsDialog.displayName = 'ClaimEventsDialog';
